import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import FileIcon from '@pelckmans/elementis/icons/file';
import LinkOnIcon from '@pelckmans/elementis/icons/link-on';
import MaterialVideoIcon from '@pelckmans/elementis/icons/material-video';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UserMaterial } from '../../api/user-material';
import ModalContext from '../../contexts/ModalContext';
import AddFileMaterialModal from '../elementis/components/add-material-modal/add-file-material-modal';
import AddLinkMaterialModal from '../elementis/components/add-material-modal/add-link-material-modal';
import { TOKEN_ACCESSOR } from '../../utils/tokenAccessor';
import AddBookwidgetDropdownItem from '../add-bookwidget-dropdown-item';
import { Module } from '../../api/module';

const EXTRA_PAYLOAD = { targetGroup: 'embed', embedType: 'bundle' } as const;

const BASE = 'PAGES.TOC_LEVEL.SECTIONS.TEACHER.MATERIAL';

interface Props {
  module: Module;
  nodeId: string;
  onMaterialAdded: (material: UserMaterial) => void;
}

const ACTION_ADD_LINK_MATERIAL = 1;
const ACTION_ADD_YOUTUBE_MATERIAL = 2;
const ACTION_ADD_FILE_MATERIAL = 3;

export default function AddBundleMaterialButton({ module, nodeId, onMaterialAdded }: Props) {
  const { t } = useTranslation();
  const { showModal } = useContext(ModalContext);

  function onSubmit(userMaterial?: UserMaterial) {
    if (userMaterial) {
      onMaterialAdded(userMaterial);
    }
  }

  function handleOnClick(actionId: number) {
    let modal: JSX.Element | undefined;

    switch (actionId) {
      case ACTION_ADD_LINK_MATERIAL:
      case ACTION_ADD_YOUTUBE_MATERIAL:
        modal = (
          <AddLinkMaterialModal
            id="add-bundle-url-material-modal"
            tokenAccessor={TOKEN_ACCESSOR}
            nodeId={nodeId}
            studioGroup={process.env.REACT_APP_P_STUDIO_GROUP}
            onSubmit={onSubmit}
            isYouTube={actionId === ACTION_ADD_YOUTUBE_MATERIAL}
            extraPayload={EXTRA_PAYLOAD}
          />
        );
        break;
      case ACTION_ADD_FILE_MATERIAL:
        modal = (
          <AddFileMaterialModal
            id="add-bundle-file-material-modal"
            tokenAccessor={TOKEN_ACCESSOR}
            nodeId={nodeId}
            studioGroup={process.env.REACT_APP_P_STUDIO_GROUP}
            onSubmit={onSubmit}
            extraPayload={EXTRA_PAYLOAD}
          />
        );
        break;
    }

    if (!modal) return;

    showModal(modal);
  }

  return (
    <ButtonDropdown
      buttonProps={{ prominence: 'secondary' }}
      title={t('SHARED_COMPONENTS.ADD_BUNDLE.MATERIAL_LIST.EMPTY.CTA')}
      disableBackgroundScroll
    >
      <ButtonDropdown.Item
        key="add-url"
        onClick={() => handleOnClick(ACTION_ADD_LINK_MATERIAL)}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_URL`)}
        textIcon={<LinkOnIcon />}
      />
      <ButtonDropdown.Item
        key="add-youtube"
        onClick={() => handleOnClick(ACTION_ADD_YOUTUBE_MATERIAL)}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_YOUTUBE`)}
        textIcon={<MaterialVideoIcon />}
      />
      <ButtonDropdown.Item
        key="add-file"
        onClick={() => handleOnClick(ACTION_ADD_FILE_MATERIAL)}
        text={t(`${BASE}.ADD_USER_MATERIAL_DROPDOWN_BUTTON.ITEMS.ADD_NEW_FILE`)}
        textIcon={<FileIcon />}
      />
      <AddBookwidgetDropdownItem
        key="add-bookwidget"
        module={module}
        nodeId={nodeId}
        onSubmit={onSubmit}
        extraPayload={EXTRA_PAYLOAD}
      />
    </ButtonDropdown>
  );
}
